import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Icon, palette } from '@m12s/component-library';
import { H5, P } from 'components/Text';
import { commentSolid } from 'lib/icons';
import BaseModal from 'components/BaseModal';
import { ModalButton } from 'components/BaseModal/styled';
import { getCustomWorkflowTriggerMessageRequired } from 'lib/selectors/getCustomWorkflowTriggerMessageRequired';
import styled from 'styled-components';

const ModalRow = styled.div`
  display: flex;
  align-items: center;
  color: ${palette.Purple500};
  margin-bottom: 1.5rem;
`;

const ModalHeaderText = styled.div`
  font-weight: 700;
  margin-left: 0.625rem;
`;

const MessageInput = styled.textarea`
  border: 1px solid ${palette.Grey300};
  border-radius: 0.25rem;
  width: 100%;
  min-height: 4rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  color: ${palette.Grey700};
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${palette.Purple500};
    box-shadow: 0 0 0 2px ${palette.Purple100};
  }

  &::placeholder {
    color: ${palette.Grey400};
  }
`;

const InputLabel = styled.label`
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  color: ${palette.Grey700};
  margin-bottom: 0.5rem;
`;

const WorkflowTriggerModal = ({
  isOpen,
  onClose,
  onSubmit,
  workflow,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');

  const isMessageRequired = useSelector(
    getCustomWorkflowTriggerMessageRequired
  );

  // const isSubmitDisabled = isMessageRequired && (!message || !message.trim());

  const handleSubmit = () => {
    // if (isSubmitDisabled) return;
    onSubmit(workflow, message);
    setMessage(''); // Clear message after submit
  };

  const handleClose = () => {
    setMessage(''); // Clear message on close
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      header={<H5 color={palette.Grey700}>{t('Trigger Workflow')}</H5>}
      details={
        <div>
          <ModalRow>
            <Icon icon={commentSolid} />
            <ModalHeaderText>{workflow?.name || t('Workflow')}</ModalHeaderText>
          </ModalRow>

          {workflow?.evaluation?.description && (
            <P color={palette.Grey600} style={{ marginBottom: '1.5rem' }}>
              {workflow.evaluation.description}
            </P>
          )}

          <div>
            <InputLabel>
              {isMessageRequired
                ? t('Additional Information')
                : t('Message (Optional)')}
            </InputLabel>
            <MessageInput
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
              }}
              placeholder={t(
                'Add a message to provide context for this workflow trigger...'
              )}
              maxLength={500}
            />
            <P
              color={palette.Grey500}
              style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}
            >
              {message.length}/500
            </P>
          </div>
        </div>
      }
      buttons={[
        <ModalButton
          key="cancel"
          variant="tertiary"
          onClick={handleClose}
          disabled={loading}
        >
          {t('Cancel')}
        </ModalButton>,
        // loading || isSubmitDisabled
        <ModalButton key="trigger" onClick={handleSubmit} disabled={loading}>
          {loading ? t('Triggering...') : t('Trigger Workflow')}
        </ModalButton>,
      ]}
    />
  );
};

WorkflowTriggerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  workflow: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    evaluation: PropTypes.shape({
      description: PropTypes.string,
    }),
  }),
  loading: PropTypes.bool,
};

export default WorkflowTriggerModal;
